import React, { FC, Fragment, useMemo } from 'react';
import { StyleSheet, Text, View } from '@react-pdf/renderer';
import { BalanceToken, Network } from '@apolloGenerated';
import { useTranslation } from 'react-i18next';
import { getNetworkEnum } from '@helpers/address';
import { format, formatPowAmount } from '@aml/amount';
import { NetworkIconEnum } from '@shared/ui';

type Props = {
  network: string;
  balance?: {
    balance: string | number;
    balanceIn: string | number;
    balanceOut: string | number;
  };
  tokens?: BalanceToken[];
};

const styles = StyleSheet.create({
  groupBetween: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  groupVertical: {
    flexDirection: 'column',
    paddingRight: 6,
    paddingLeft: 6,
    borderRightWidth: 1,
    borderColor: '#dfe2eb',
    marginBottom: 12,
  },
  title: {
    fontSize: 8,
    color: '#69789F',
    marginBottom: 4,
    marginRight: 4,
  },
  subtitle: {
    fontSize: 8,
    color: '#69789F',
    marginRight: 8,
  },
  text: {
    fontSize: 8,
    color: '#000',
  },
  textRed: {
    fontSize: 8,
    color: '#B8433D',
  },
  textGreen: {
    fontSize: 8,
    color: '#3D7A21',
  },
  textSmall: {
    fontSize: 8,
  },
});

export const BalancePDF: FC<Props> = ({ network, balance, tokens }) => {
  const { t } = useTranslation();

  const formatBalance = (total: string) => format(parseFloat(total).toFixed(2), 2);

  const renderForBTC = useMemo(
    () => (
      <View style={styles.groupVertical}>
        <View style={styles.groupBetween}>
          <Text style={styles.title}>{t('strings.balance')}</Text>
          <Text style={styles.textSmall}>{formatBalance(String(balance?.balance))}</Text>
        </View>
        <View style={styles.groupBetween}>
          <Text style={styles.title}>{t('strings.received')}</Text>
          <Text style={[styles.textSmall, styles.textGreen]}>
            +{formatBalance(String(balance?.balanceIn))}
          </Text>
        </View>
        <View style={styles.groupBetween}>
          <Text style={styles.title}>{t('strings.sent')}</Text>
          <Text style={(styles.textSmall, styles.textRed)}>
            -{formatBalance(String(balance?.balanceOut))}
          </Text>
        </View>
      </View>
    ),
    [],
  );

  const findToken = (token: string) => {
    return tokens?.find((item) => item.symbol.toLowerCase() === token.toLowerCase());
  };

  const formatAmount = (balance: string | number, decimals: number) => {
    return format(parseFloat(String(formatPowAmount(balance, decimals))).toFixed(2), 2);
  };

  const renderForTRON = useMemo(() => {
    const usdt = findToken(NetworkIconEnum.Usdt);
    const trx = findToken(NetworkIconEnum.Trx);

    return (
      <View style={styles.groupVertical}>
        <View style={styles.groupBetween}>
          <Text style={styles.title}>{NetworkIconEnum.Usdt}</Text>
          <Text style={styles.textSmall}>
            {usdt ? formatAmount(usdt.balance, usdt.decimals) : '0.00'}
          </Text>
        </View>
        <View style={styles.groupBetween}>
          <Text style={styles.title}>{NetworkIconEnum.Trx}</Text>
          <Text style={styles.textSmall}>
            {trx ? formatAmount(trx.balance, trx.decimals) : '0.00'}
          </Text>
        </View>
      </View>
    );
  }, []);

  const renderForETH = useMemo(() => {
    const usdt = findToken(NetworkIconEnum.Usdt);
    const eth = findToken(NetworkIconEnum.Eth);

    return (
      <View style={styles.groupVertical}>
        <View style={styles.groupVertical}>
          <View style={styles.groupBetween}>
            <Text style={styles.title}>{NetworkIconEnum.Usdt}</Text>
            <Text style={styles.textSmall}>
              {usdt ? formatAmount(usdt.balance, usdt.decimals) : '0.00'}
            </Text>
          </View>
          <View style={styles.groupBetween}>
            <Text style={styles.title}>{NetworkIconEnum.Eth}</Text>
            <Text style={styles.textSmall}>
              {eth ? formatAmount(eth.balance, eth.decimals) : '0.00'}
            </Text>
          </View>
        </View>
      </View>
    );
  }, []);

  return useMemo(() => {
    switch (getNetworkEnum(network)) {
      case Network.Btc:
        return renderForBTC;
      case Network.Tron:
        return renderForTRON;
      case Network.Eth:
        return renderForETH;
      default:
        return <></>;
    }
  }, [network, balance, tokens]);
};
